/**
 * Sound Effects Hook
 *
 * Manages audio playback for the app
 *
 * Sound mapping:
 * - click: Button presses, calculator keypad
 * - vault-click: Vault dial press
 * - bonus-explode: Bonus particle explosion
 * - counter-tick: Numbers counting up animation
 * - bonus-complete: Bonus animation finished (cash register)
 * - background: Background music (low volume, looped)
 */

import { useCallback, useRef, useEffect, useState } from 'react';

// Sound file paths (relative to public/)
const SOUNDS = {
  click: '/sounds/click.mp3',
  vaultClick: '/sounds/vault-click.mp3',
  bonusExplode: '/sounds/bonus-explode.mp3',
  counterTick: '/sounds/counter-tick.mp3',
  bonusComplete: '/sounds/bonus-complete.mp3',
  background: '/sounds/background.mp3',
} as const;

type SoundName = keyof typeof SOUNDS;

// Default volumes (0-1)
const DEFAULT_VOLUMES: Record<SoundName, number> = {
  click: 0.5,
  vaultClick: 0.6,
  bonusExplode: 0.7,
  counterTick: 0.5,
  bonusComplete: 0.7,
  background: 0.06, // Background music volume (very subtle)
};

// Global mute state (persisted in localStorage)
let globalMuted = localStorage.getItem('soundMuted') === 'true';
const muteListeners = new Set<(muted: boolean) => void>();

function setGlobalMuted(muted: boolean) {
  globalMuted = muted;
  localStorage.setItem('soundMuted', String(muted));
  muteListeners.forEach((listener) => listener(muted));
}

// Audio cache to avoid re-creating Audio objects
const audioCache = new Map<string, HTMLAudioElement>();

function getAudio(src: string): HTMLAudioElement {
  if (!audioCache.has(src)) {
    const audio = new Audio(src);
    audio.preload = 'auto';
    audioCache.set(src, audio);
  }
  return audioCache.get(src)!;
}

/**
 * Hook for playing one-shot sound effects
 */
export function useSound() {
  const play = useCallback((name: SoundName, volumeOverride?: number) => {
    if (globalMuted) return; // Skip if muted

    try {
      const src = SOUNDS[name];
      const audio = getAudio(src);

      // Clone for overlapping plays
      const clone = audio.cloneNode() as HTMLAudioElement;
      clone.volume = volumeOverride ?? DEFAULT_VOLUMES[name];
      clone.play().catch(() => {
        // Ignore autoplay errors (user hasn't interacted yet)
      });
    } catch (err) {
      console.warn(`[useSound] Failed to play ${name}:`, err);
    }
  }, []);

  return { play };
}

/**
 * Hook for background music with controls
 */
export function useBackgroundMusic(autoPlay = false) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);
  const [isMuted, setIsMuted] = useState(globalMuted);

  useEffect(() => {
    const audio = new Audio(SOUNDS.background);
    audio.loop = true;
    audio.volume = DEFAULT_VOLUMES.background;
    audio.preload = 'auto';
    audioRef.current = audio;

    if (autoPlay && !globalMuted) {
      audio.play().catch(() => {
        // Autoplay blocked - will play on first user interaction
      });
    }

    // Listen for mute changes
    const handleMuteChange = (muted: boolean) => {
      setIsMuted(muted);
      if (muted && audioRef.current) {
        audioRef.current.pause();
        isPlayingRef.current = false;
      }
    };
    muteListeners.add(handleMuteChange);

    return () => {
      audio.pause();
      audio.src = '';
      muteListeners.delete(handleMuteChange);
    };
  }, [autoPlay]);

  const play = useCallback(() => {
    if (audioRef.current && !isPlayingRef.current && !globalMuted) {
      audioRef.current.play().catch(() => {});
      isPlayingRef.current = true;
    }
  }, []);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      isPlayingRef.current = false;
    }
  }, []);

  const toggle = useCallback(() => {
    if (isPlayingRef.current) {
      pause();
    } else {
      play();
    }
  }, [play, pause]);

  const setVolume = useCallback((volume: number) => {
    if (audioRef.current) {
      audioRef.current.volume = Math.max(0, Math.min(1, volume));
    }
  }, []);

  return {
    play,
    pause,
    toggle,
    setVolume,
    isPlaying: isPlayingRef.current,
    isMuted,
  };
}

/**
 * Hook for global sound mute control
 */
export function useSoundMute() {
  const [isMuted, setIsMuted] = useState(globalMuted);

  useEffect(() => {
    const handleMuteChange = (muted: boolean) => {
      setIsMuted(muted);
    };
    muteListeners.add(handleMuteChange);
    return () => {
      muteListeners.delete(handleMuteChange);
    };
  }, []);

  const toggleMute = useCallback(() => {
    setGlobalMuted(!globalMuted);
  }, []);

  const mute = useCallback(() => {
    setGlobalMuted(true);
  }, []);

  const unmute = useCallback(() => {
    setGlobalMuted(false);
  }, []);

  return { isMuted, toggleMute, mute, unmute };
}

/**
 * Preload all sounds (call on app init)
 */
export function preloadSounds() {
  Object.values(SOUNDS).forEach((src) => {
    getAudio(src);
  });
}

export { SOUNDS, DEFAULT_VOLUMES };
export type { SoundName };
